import React, { useEffect, useRef, useState } from 'react';
import { EditorMode } from '../../../types/fingeringEditor';
import SaveSplitButton from './SaveSplitButton';
import SaveActionsDropdown from './SaveActionsDropdown';
import TuningSelector from './TuningSelector';
import MobileBottomSheet from './MobileBottomSheet';
import BpmAdjustControl from './BpmAdjustControl';
import { AutoSaveToggle } from './AutoSaveToggle';
import { NotifyToggle } from './NotifyToggle';
import { features } from '../../../config/features';
import {
  getAutoSaveEnabled,
  setAutoSaveEnabled as persistAutoSaveEnabled,
} from '../../../utils/autoSaveSettings';

const HINT_MESSAGES = [
  '楽譜上をタップして配置',
  '既存の運指をタップして和音を追加',
] as const;
/** swap アニメーションの半分の時間（opacity=0 に達する midpoint）。tailwind の 3s と同期 */
const HINT_SWAP_MID_MS = 1500;
const ZOOM_STEP = 0.5;
const MAX_ZOOM = 5;

interface EditorToolbarProps {
  addMode: boolean;
  onToggleAddMode: () => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onSaveAndRegenerate: () => void;
  isGenerating: boolean;
  isMobile: boolean;
  editorMode: EditorMode;
  onEditorModeChange: (mode: EditorMode) => void;
  canUndo: boolean;
  onUndo: () => void;
  minZoom?: number;
  currentBpm: number;
  detectedBpm: number | undefined;
  onBpmChange: (bpm: number) => void;
  bpmDirty: boolean;
  onGeneratePdfOnly: () => void;
  onGenerateAudioOnly: () => void;
  isGeneratingAudio: boolean;
  /** 現在の scordatura ('' = 標準 / 'drop_d' = Drop D)。チューニング選択 UI の初期値に使う。 */
  currentScordatura?: string;
  /** チューニング変更 → モーダル確認 → from_pdf_reassign 経路。引数は選択された scordatura。 */
  onTuningChangeRegenerate?: (scordatura: string) => void;
  // issue #16 v3 Phase A: selectMode toggle は撤廃済 (selectMode は Core 派生値)。
  // Mobile では editorMode (edit/move) toggle が範囲選択 ON/OFF も兼ねる。 sticky 一括削除 bar のみ残存。
  selectedCount?: number;
  onDeleteMany?: () => void;
  onClearSelection?: () => void;
  // issue #168: 運指コピー＆ペースト配線 (ClipboardActionBar を統合、 optional)。
  //   - onCopy: selection-action-bar 内コピーボタン (選択中に表示)。
  //   - hasClipboard / onPaste: addButton 隣のペーストボタン (clipboard 有 && 非配置)。
  //   - placementActive / onCancel: 配置モード中のインライン案内 + キャンセル。
  hasClipboard?: boolean;
  placementActive?: boolean;
  onCopy?: () => void;
  onPaste?: () => void;
  onCancel?: () => void;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  addMode, onToggleAddMode, zoom, onZoomChange,
  onSaveAndRegenerate, isGenerating,
  isMobile, editorMode, onEditorModeChange,
  canUndo, onUndo, minZoom = 1,
  currentBpm, detectedBpm, onBpmChange,
  bpmDirty, onGeneratePdfOnly, onGenerateAudioOnly, isGeneratingAudio,
  currentScordatura = '', onTuningChangeRegenerate,
  selectedCount = 0, onDeleteMany, onClearSelection,
  hasClipboard = false, placementActive = false, onCopy, onPaste, onCancel,
}) => {
  const [hintIndex, setHintIndex] = useState(0);
  const [hintPhase, setHintPhase] = useState<'pulse' | 'swap'>('pulse');
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
  // issue #148: 「保存設定」 popover の開閉と、 自動保存 ON/OFF (localStorage SSOT)。
  // EditorToolbarProps は変更せず、 生成フォーム側と同じ localStorage を読み書きして自己完結する。
  const [saveSettingsOpen, setSaveSettingsOpen] = useState(false);
  const [editorAutoSave, setEditorAutoSave] = useState<boolean>(() => getAutoSaveEnabled());
  const handleEditorAutoSaveChange = (next: boolean): void => {
    persistAutoSaveEnabled(next);
    setEditorAutoSave(next);
  };
  // チューニング変更 UI 展開状態と選択値。閉じると一旦リセット。
  const [tuningPanelOpen, setTuningPanelOpen] = useState(false);
  const [pendingTuning, setPendingTuning] = useState<string>(currentScordatura);
  useEffect(() => {
    if (tuningPanelOpen) setPendingTuning(currentScordatura);
  }, [tuningPanelOpen, currentScordatura]);

  useEffect(() => {
    if (!addMode) {
      setHintIndex(0);
      setHintPhase('pulse');
    }
  }, [addMode]);

  // swap phase 中央（opacity=0 の瞬間）でテキスト差し替え
  useEffect(() => {
    if (!addMode || hintPhase !== 'swap') return;
    const id = setTimeout(() => {
      setHintIndex((i) => (i + 1) % HINT_MESSAGES.length);
    }, HINT_SWAP_MID_MS);
    return () => clearTimeout(id);
  }, [hintPhase, addMode]);

  const handleHintIteration = () => {
    setHintPhase((p) => (p === 'pulse' ? 'swap' : 'pulse'));
  };

  const handleSaveMainClick = () => {
    setSaveMenuOpen(false);
    onSaveAndRegenerate();
  };

  const handleToggleDropdown = () => {
    // issue #139: 音源 OFF 時は ▼ 自体を非表示にするため通常は呼ばれないが、防御的にガード。
    if (!features.enableAudio) return;
    setSaveMenuOpen((prev) => !prev);
  };

  // ============================================================
  // 共通要素 (Mobile / Desktop の両方で使う) を関数に切り出して DRY 化。
  // ============================================================

  // issue #26 v7 Phase 3': edit/move トグル (compact + label 維持 + active font-bold)。
  // Mobile のみ描画。 サイズ: text-[11px] / px-2 py-1 / svg w-3 h-3。
  const modeToggleEl = isMobile ? (
    <div className="flex rounded-xl overflow-hidden border border-slate-600">
      <button
        type="button"
        onClick={() => onEditorModeChange('edit')}
        aria-pressed={editorMode === 'edit'}
        className={`px-2 py-1 text-[11px] transition-all flex items-center gap-1
          ${editorMode === 'edit'
            ? 'bg-amber-500/20 text-amber-400 border-amber-500 font-bold'
            : 'bg-slate-700/50 text-slate-500 border-slate-600 hover:bg-slate-700 font-semibold'}`}
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
        編集
      </button>
      <button
        type="button"
        onClick={() => onEditorModeChange('move')}
        aria-pressed={editorMode === 'move'}
        className={`px-2 py-1 text-[11px] transition-all flex items-center gap-1
          ${editorMode === 'move'
            ? 'bg-amber-500/20 text-amber-400 border-amber-500 font-bold'
            : 'bg-slate-700/50 text-slate-500 border-slate-600 hover:bg-slate-700 font-semibold'}`}
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
        運指移動
      </button>
    </div>
  ) : null;

  // issue #26 v7 Phase 2': zoom 3 要素縮約 (リセット撤廃 + aria-label、 rejected `7394d2b` 流用)。
  const zoomEl = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label="ズームアウト"
        onClick={() => onZoomChange(Math.max(minZoom, zoom - ZOOM_STEP))}
        className="w-8 h-8 rounded-lg border border-slate-600 bg-slate-700/50 text-slate-400 text-base hover:border-slate-500 hover:bg-slate-700"
      >−</button>
      <span className="text-[11px] text-slate-500 min-w-[40px] text-center tabular-nums">{Math.round(zoom * 100)}%</span>
      <button
        type="button"
        aria-label="ズームイン"
        onClick={() => onZoomChange(Math.min(MAX_ZOOM, zoom + ZOOM_STEP))}
        className="w-8 h-8 rounded-lg border border-slate-600 bg-slate-700/50 text-slate-400 text-base hover:border-slate-500 hover:bg-slate-700"
      >+</button>
    </div>
  );

  const undoEl = isMobile ? (
    <button
      onClick={onUndo}
      disabled={!canUndo}
      aria-label="元に戻す"
      className={`w-8 h-8 rounded-lg border transition-all flex items-center justify-center
        ${canUndo
          ? 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500 hover:bg-slate-700'
          : 'border-slate-700 bg-slate-800/50 text-slate-600 cursor-not-allowed'}`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" />
      </svg>
    </button>
  ) : null;

  // issue #170: move モードでも「＋ 追加」は常時活性。 押下で Core 側が edit へ矯正する。
  const addButtonEl = (
    <button
      onClick={onToggleAddMode}
      className={`px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-semibold text-[13px] md:text-sm transition-all
        ${addMode
          ? 'border-2 border-amber-500 bg-amber-950/50 text-amber-400'
          : 'border border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500 hover:bg-slate-700'
        }`}
    >
      {addMode ? '＋ 配置モード ON' : '＋ 追加'}
    </button>
  );

  const addHintEl = addMode ? (
    <span
      className={`text-xs text-amber-400 truncate min-w-0 ${hintPhase === 'swap' ? 'animate-hint-swap' : 'animate-hint-pulse'}`}
      onAnimationIteration={handleHintIteration}
    >
      {HINT_MESSAGES[hintIndex]}
    </span>
  ) : null;

  // issue #168: ペーストボタン (clipboard 有 && 非配置)。 addButton の隣に置き、 押下で配置モードへ入る。
  const pasteButtonEl = hasClipboard && !placementActive ? (
    <button
      type="button"
      data-testid="clipboard-paste-button"
      onClick={onPaste}
      aria-label="運指をペースト（配置モード）"
      className="px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-semibold text-[13px] md:text-sm flex-shrink-0 transition-all
        border border-sky-500/60 bg-sky-600/20 text-sky-200 hover:bg-sky-600/40 hover:border-sky-500"
    >
      📌 ペースト
    </button>
  ) : null;

  // issue #168: 配置モード中のインライン案内 (addHintEl 方式) + キャンセルボタン。
  const placementHintEl = placementActive ? (
    <>
      <span
        data-testid="clipboard-placement-hint"
        className="text-xs text-sky-300 truncate min-w-0 animate-hint-pulse"
        aria-live="polite"
      >
        {isMobile ? '譜面に触れて配置（離すと確定）' : 'クリックで配置 / Esc で中止'}
      </span>
      <button
        type="button"
        data-testid="clipboard-cancel-button"
        onClick={onCancel}
        aria-label="配置モードを中止"
        className="px-3 py-1 rounded-md border border-slate-500 bg-slate-700/40 text-slate-200 hover:bg-slate-600 text-[12px] font-semibold flex-shrink-0 transition-colors"
      >
        ✕ キャンセル
      </button>
    </>
  ) : null;

  // issue #16 Phase 7 + #26 Phase 5b (plan v5.1 §2.2): 選択中の sticky 一括削除 bar (N 個選択 + 一括削除 + 解除)。
  // Phase 5b 後の責務分離:
  //   - Desktop: EditorToolbar 内に embed (現状維持)
  //   - Mobile: Mobile page 直下 (`mobile-sticky-delete-bar`、 画面下部 fixed) に移設 → EditorToolbar 経路から除外
  const selectionBarEl = !isMobile && selectedCount > 0 ? (
    <div
      data-testid="selection-action-bar"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-amber-500/60 bg-amber-500/15 text-amber-300 text-[12px] font-semibold"
    >
      <span aria-live="polite">{selectedCount} 個選択</span>
      <button
        type="button"
        onClick={onDeleteMany}
        aria-label="選択中の運指を一括削除"
        className="px-2 py-0.5 rounded-md border border-amber-500/60 bg-amber-600/20 text-amber-200 hover:bg-amber-600/40 hover:text-white transition-colors"
      >
        🗑 一括削除
      </button>
      {/* issue #168: 選択中の運指をコピー (ClipboardActionBar から統合)。 selection bar のテーマに合わせ amber。 */}
      {onCopy && (
        <button
          type="button"
          data-testid="clipboard-copy-button"
          onClick={onCopy}
          aria-label="選択中の運指をコピー"
          className="px-2 py-0.5 rounded-md border border-amber-500/60 bg-amber-600/20 text-amber-200 hover:bg-amber-600/40 hover:text-white transition-colors"
        >
          📋 コピー
        </button>
      )}
      <button
        type="button"
        onClick={onClearSelection}
        aria-label="選択を解除"
        className="px-2 py-0.5 rounded-md border border-slate-500 bg-slate-700/40 text-slate-300 hover:bg-slate-600 transition-colors"
      >
        ✕ 解除
      </button>
    </div>
  ) : null;

  // issue #26 Phase 7-3: tuning 中身は Mobile bottom sheet と PC popover で共通利用。
  const tuningSheetContent = (
    <>
      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
        チューニングを選択
      </div>
      <TuningSelector
        value={pendingTuning || 'standard'}
        options={[
          { value: 'standard', label: '標準' },
          { value: 'drop_d', label: 'Drop D' },
        ]}
        onChange={(next) => setPendingTuning(next === 'standard' ? '' : next)}
      />
      <p className="mt-3 text-[11px] text-gray-400 leading-snug">
        再生成すると、編集中の運指は破棄されます。
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setTuningPanelOpen(false)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          キャンセル
        </button>
        <button
          type="button"
          onClick={() => {
            setTuningPanelOpen(false);
            onTuningChangeRegenerate?.(pendingTuning);
          }}
          className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 shadow-sm transition-colors"
        >
          再生成実行
        </button>
      </div>
    </>
  );

  // issue #26 Phase 7-3: Tuning は Mobile = MobileBottomSheet、 PC = popover 下開き (Phase 4 上開きから revert)。
  const tuningEl = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setTuningPanelOpen((v) => !v)}
        disabled={isGenerating}
        className={`px-3 md:px-4 py-1.5 md:py-2.5 rounded-xl font-semibold text-[12px] md:text-[13px] transition-all
          ${tuningPanelOpen
            ? 'border border-amber-400 bg-amber-500/20 text-amber-300'
            : 'border border-amber-600/60 bg-amber-600/15 text-amber-300 hover:bg-amber-600/25 hover:border-amber-500'}
          ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        🎸 チューニング変更
      </button>
      {tuningPanelOpen && (
        isMobile ? (
          <MobileBottomSheet
            open={tuningPanelOpen}
            onClose={() => setTuningPanelOpen(false)}
            ariaLabel="チューニング変更"
          >
            <div className="bg-white rounded-lg p-4">
              {tuningSheetContent}
            </div>
          </MobileBottomSheet>
        ) : (
          // PC: 下開き (Phase 4 上開きから revert)。 spec §2.2.4: clip 再発時は createPortal 切替を検討 (実機検証後判定)。
          <div data-testid="tuning-popover" className="absolute right-0 top-full mt-2 w-72 rounded-xl bg-white shadow-xl border border-gray-200 p-4 z-50 animate-popin">
            {tuningSheetContent}
          </div>
        )
      )}
    </div>
  );

  // issue #26 Phase 7-3: SaveActionsSheetContent — Mobile bottom sheet 内で SaveActionsDropdown の中身を再現。
  //   SaveActionsDropdown 本体は PC 専用 (role=menu)、 Mobile はこちらの sheet 用版を使う。
  const handleMobileSavePdf = () => {
    if (isGenerating) return;
    setSaveMenuOpen(false);
    onGeneratePdfOnly();
  };
  const handleMobileSaveAudio = () => {
    if (isGeneratingAudio) return;
    setSaveMenuOpen(false);
    onGenerateAudioOnly();
  };
  const saveActionsSheetContent = (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleMobileSavePdf}
        aria-disabled={isGenerating}
        aria-label="PDFのみ生成"
        className={`w-full px-4 py-3 text-left text-base text-slate-200 hover:bg-slate-700 rounded-lg flex items-center gap-3 ${
          isGenerating ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        <span aria-hidden="true">📄</span>
        <span>PDFのみ生成</span>
      </button>
      {features.enableAudio && (
        <>
          <button
            type="button"
            onClick={handleMobileSaveAudio}
            aria-disabled={isGeneratingAudio}
            aria-label="音源のみ生成"
            className={`w-full px-4 py-3 text-left text-base text-slate-200 hover:bg-slate-700 rounded-lg flex items-center gap-3 ${
              isGeneratingAudio ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <span aria-hidden="true">♪</span>
            <span>{isGeneratingAudio ? '生成中...' : '音源のみ生成'}</span>
            {bpmDirty && (
              <span
                data-testid="audio-only-dirty-dot-mobile"
                className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400"
              />
            )}
          </button>
          <div className="border-t border-slate-600 my-1" />
          <BpmAdjustControl
            currentBpm={currentBpm}
            detectedBpm={detectedBpm}
            onChange={onBpmChange}
          />
        </>
      )}
    </div>
  );

  // issue #26 Phase 7-3: Save は Mobile = MobileBottomSheet、 PC = SaveActionsDropdown (role=menu、 不変)。
  const saveEl = (
    <div className="relative">
      <SaveSplitButton
        onSaveAndRegenerate={handleSaveMainClick}
        onOpenDropdown={handleToggleDropdown}
        isOpen={saveMenuOpen}
        isGenerating={isGenerating}
        bpmDirty={bpmDirty}
        dropdownTriggerRef={dropdownTriggerRef}
      />
      {saveMenuOpen && features.enableAudio && (
        isMobile ? (
          <MobileBottomSheet
            open={saveMenuOpen}
            onClose={() => setSaveMenuOpen(false)}
            ariaLabel="保存アクション"
          >
            {saveActionsSheetContent}
          </MobileBottomSheet>
        ) : (
          <SaveActionsDropdown
            anchorRef={dropdownTriggerRef}
            onClose={() => setSaveMenuOpen(false)}
            onGeneratePdfOnly={onGeneratePdfOnly}
            onGenerateAudioOnly={onGenerateAudioOnly}
            isGenerating={isGenerating}
            isGeneratingAudio={isGeneratingAudio}
            isMobile={isMobile}
            currentBpm={currentBpm}
            detectedBpm={detectedBpm}
            onBpmChange={onBpmChange}
            bpmDirty={bpmDirty}
          />
        )
      )}
    </div>
  );

  // issue #148: 「保存設定」 中身 (保存方法 + メール通知)。 生成フォーム側と同一部品を再利用する。
  //   AutoSaveToggle は controlled (editorAutoSave / handleEditorAutoSaveChange で localStorage 永続)、
  //   NotifyToggle は自身の localStorage を SSOT とし、 自動保存 OFF 時は disabled で淡色化する。
  const saveSettingsContent = (
    <>
      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
        保存設定
      </div>
      <div className="flex flex-col gap-3">
        <AutoSaveToggle enabled={editorAutoSave} onChange={handleEditorAutoSaveChange} />
        <NotifyToggle disabled={!editorAutoSave} />
      </div>
    </>
  );

  // issue #148: Tuning / Save と同じく Mobile = MobileBottomSheet、 PC = popover 下開き。
  const saveSettingsEl = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setSaveSettingsOpen((v) => !v)}
        aria-label="保存設定"
        aria-expanded={saveSettingsOpen}
        className={`px-3 md:px-4 py-1.5 md:py-2.5 rounded-xl font-semibold text-[12px] md:text-[13px] transition-all
          ${saveSettingsOpen
            ? 'border border-slate-400 bg-slate-600/40 text-slate-100'
            : 'border border-slate-600 bg-slate-700/50 text-slate-300 hover:bg-slate-700 hover:border-slate-500'}`}
      >
        ⚙️ 保存設定
      </button>
      {saveSettingsOpen && (
        isMobile ? (
          <MobileBottomSheet
            open={saveSettingsOpen}
            onClose={() => setSaveSettingsOpen(false)}
            ariaLabel="保存設定"
          >
            <div className="bg-white rounded-lg p-4">
              {saveSettingsContent}
            </div>
          </MobileBottomSheet>
        ) : (
          <div data-testid="save-settings-popover" className="absolute right-0 top-full mt-2 w-72 rounded-xl bg-white shadow-xl border border-gray-200 p-4 z-50 animate-popin">
            {saveSettingsContent}
          </div>
        )
      )}
    </div>
  );

  // ============================================================
  // Layout: Mobile = 3 段固定 (issue #26 v7 Phase 5'')、 Desktop = 単一 1 段 flex-wrap (現状維持)
  // ============================================================
  if (isMobile) {
    // issue #26 v7 Phase 5'': Mobile 3 段組み (ユーザー直接指示 2026-06-01)。
    //   1 段目: mode 切替 (左) + zoom (右)
    //   2 段目: undo + ＋追加 + (hint) + 範囲選択 UI (右、 条件)
    //   3 段目: チューニング (左、 popover 上開き) + 保存 (右)
    return (
      <div className="bg-slate-800/90 backdrop-blur-sm border-b border-slate-700/60 flex-shrink-0 z-10">
        <div
          data-testid="editor-toolbar-row-1"
          className="px-3 py-1.5 flex gap-2 items-center flex-nowrap"
        >
          {modeToggleEl}
          <div className="flex-1" />
          {zoomEl}
        </div>
        <div
          data-testid="editor-toolbar-row-2"
          className="px-3 py-1.5 flex gap-2 items-center flex-nowrap border-t border-slate-700/40"
        >
          {undoEl}
          {addButtonEl}
          {addHintEl}
          {pasteButtonEl}
          {placementHintEl}
          <div className="flex-1" />
          {/* issue #26 Phase 5b (plan v5.1 §2.2): selectionBarEl は Mobile では Mobile page 直下 (mobile-sticky-delete-bar) に移設、 toolbar には残さない。 */}
        </div>
        <div
          data-testid="editor-toolbar-row-3"
          className="px-3 py-1.5 flex gap-2 items-center flex-nowrap border-t border-slate-700/40"
        >
          {tuningEl}
          <div className="flex-1" />
          {saveSettingsEl}
          {saveEl}
        </div>
      </div>
    );
  }

  // Desktop: 現状の単一 1 段 flex-wrap を維持 (PC 影響ゼロ、 plan v7 §3.5)。
  //   要素順は元コードと同じ: addButton → hint → selectionBar → spacer → zoom → tuning → save。
  return (
    <div className="px-4 py-2 md:px-5 md:py-3 bg-slate-800/90 backdrop-blur-sm border-b border-slate-700/60 flex gap-2 md:gap-3 items-center flex-shrink-0 z-10 flex-wrap">
      {addButtonEl}
      {addHintEl}
      {pasteButtonEl}
      {placementHintEl}
      {selectionBarEl}
      <div className="flex-1" />
      {zoomEl}
      {tuningEl}
      {saveSettingsEl}
      {saveEl}
    </div>
  );
};

export default EditorToolbar;
